// Simple, testable wrappers for Meta Pixel pageview tracking

declare global {
    interface Window {
        fbq?: (
            action: string,
            eventName: string,
            parameters?: Record<string, unknown>,
            options?: Record<string, unknown>
        ) => void;
    }
}

export type AfterNav = (cb: () => void) => void;

/** Fire a PageView if the pixel is present (no-throw). */
export function trackPageView(): void {
    try {
        if (
            typeof globalThis.window !== 'undefined' &&
            typeof globalThis.window.fbq === 'function'
        ) {
            globalThis.window.fbq('track', 'PageView');
        }
    } catch {
        // swallow (test-friendly)
    }
}

/** Load Meta Pixel script and initialize with pixel ID */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* c8 ignore start */
function loadMetaPixel(pixelId: string): void {
    try {
        // Load Meta Pixel script
        (function (f: any, b: any, e: any, v: any, n: any, t: any, s: any): void {
            if (f.fbq) return;
            n = f.fbq = function (): void {
                n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
            };
            if (!f._fbq) f._fbq = n;
            n.push = n;
            n.loaded = !0;
            n.version = '2.0';
            n.queue = [];
            t = b.createElement(e);
            t.async = !0;
            t.src = v;
            s = b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t, s);
        })(
            globalThis.window,
            globalThis.document,
            'script',
            'https://connect.facebook.net/en_US/fbevents.js',
            null,
            null,
            null
        );

        // Initialize Meta Pixel with the correct ID
        if (globalThis.window?.fbq) {
            console.log('Meta Pixel: Initializing fbq with pixel ID:', pixelId);
            globalThis.window.fbq('init', pixelId);
            console.log('Meta Pixel: fbq initialized successfully');
        } else {
            console.log('Meta Pixel: fbq not available after script load');
        }
    } catch {
        // Swallow errors in test environment or when DOM is not available
    }
}
/* c8 ignore end */
/* eslint-enable @typescript-eslint/no-explicit-any */

export interface PageEvent {
    name: string;
    oncePerSession?: boolean;
    parameters?: Record<string, unknown>;
    eventID?: string;
}

/** Fire a page-specific event if the pixel is present (no-throw). */
export function trackPageEvent(event: PageEvent): void {
    try {
        if (
            typeof globalThis.window !== 'undefined' &&
            typeof globalThis.window.fbq === 'function'
        ) {
            const sessionKey = `fbq_${event.name.toLowerCase()}_fired`;

            // Check if this event should only fire once per session
            if (event.oncePerSession && globalThis.sessionStorage?.getItem(sessionKey)) {
                console.log(`Meta Pixel ${event.name}: Already fired in this session, skipping`);
                return;
            }

            // Fire the event
            if (event.parameters && event.eventID) {
                globalThis.window.fbq('track', event.name, event.parameters, {
                    eventID: event.eventID
                });
            } else if (event.parameters) {
                globalThis.window.fbq('track', event.name, event.parameters);
            } else {
                globalThis.window.fbq('track', event.name);
            }

            // Mark as fired if it's a once-per-session event
            if (event.oncePerSession) {
                globalThis.sessionStorage?.setItem(sessionKey, '1');
            }

            console.log(`Meta Pixel ${event.name}: Event fired successfully`);
        }
    } catch (error) {
        console.error(`Meta Pixel ${event.name}: Error firing event`, error);
    }
}

/** Register pageview tracking on initial load + every client-side route change. */
export function registerPageViewTracking(
    afterNav: AfterNav,
    enabled: boolean,
    pixelId?: string,
    pageEvent?: PageEvent | null
): void {
    if (!enabled) return;

    // Load and initialize Meta Pixel if pixelId is provided
    if (pixelId) {
        loadMetaPixel(pixelId);
    }

    afterNav(() => {
        trackPageView(); // fires once after hydration (from===null) and on each route change

        // Fire page-specific event if configured
        if (pageEvent) {
            trackPageEvent(pageEvent);
        }
    });
}

/** Register pageview tracking with dynamic store reading. */
export function registerPageViewTrackingWithStore(
    afterNav: AfterNav,
    enabled: boolean,
    pixelId?: string,
    getCurrentPageEvent?: () => PageEvent | null
): void {
    if (!enabled) return;

    // Load and initialize Meta Pixel if pixelId is provided
    if (pixelId) {
        loadMetaPixel(pixelId);
    }

    afterNav(() => {
        trackPageView(); // fires once after hydration (from===null) and on each route change

        // Fire page-specific event if configured (read current store value)
        if (getCurrentPageEvent) {
            const currentPageEvent = getCurrentPageEvent();
            if (currentPageEvent) {
                trackPageEvent(currentPageEvent);
            }
        }
    });
}
